import { supabase } from '../../lib/supabase';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { SearchResult } from './types';

/**
 * Performs a search across all content types
 */
export const searchContent = async (query: string): Promise<SearchResult[]> => {
  if (!query.trim()) {
    return [];
  }

  try {
    const searchTerm = `%${query.trim()}%`;
    const promises = [];

    // Split query into individual words for more flexible matching
    const words = query.toLowerCase().trim().split(/\s+/);
    const exactPhrase = `%${query.trim()}%`;

    // Check if query contains document type and number pattern (e.g., "Lei 1443")
    const docTypeNumberMatch = query.match(/^(lei|decreto|portaria|edital)\s+(\d+)$/i);
    const isDocTypeNumberSearch = !!docTypeNumberMatch;
    const docNumber = docTypeNumberMatch ? parseInt(docTypeNumberMatch[2]) : null;
    const docType = docTypeNumberMatch ? docTypeNumberMatch[1].toLowerCase() : null;

    // Extract numbers from query for specialized document searches
    const numbersInQuery = query.match(/\d+/g);
    const queryNumbers = numbersInQuery ? numbersInQuery.map(num => parseInt(num)) : [];

    // Get biddings with document IDs
    const { data: biddingsData, error: biddingsError } = await supabase
      .from('biddings')
      .select(`
        id,
        title,
        description,
        object,
        bidding_date,
        location,
        bidding_type,
        document_id,
        document:documents(
          number,
          year
        )
      `)
      .or(`title.ilike.${searchTerm},description.ilike.${searchTerm},object.ilike.${searchTerm}`)
      .order('bidding_date', { ascending: false })
      .limit(10);
    
    if (biddingsError) throw biddingsError;
    
    // Extract document IDs from biddings
    const biddingDocIds = (biddingsData || [])
      .map(b => b.document_id)
      .filter(Boolean);
    
    // Add biddings to results
    promises.push(Promise.resolve({ data: biddingsData }));

    // Regular content searches
    promises.push(
      supabase
        .from('news')
        .select(`
          id,
          title,
          content,
          created_at,
          updated_at,
          image_url,
          thumbnail_url,
          slug,
          categories:news_categories(name)
        `)
        .ilike('title', searchTerm)
        .or(`content.ilike.${searchTerm}`)
        .order('updated_at', { ascending: false })
        .limit(10)
    );

    promises.push(
      supabase
        .from('events')
        .select('id, title, description, date')
        .ilike('title', searchTerm)
        .or(`description.ilike.${searchTerm}`)
        .order('date', { ascending: true })
        .limit(10)
    );

    // For services, use a more flexible search approach
    // Try exact phrase match first, then fall back to word-by-word matching
    let servicesQuery = supabase
      .from('services')
      .select('id, title, description, link, icon');

    // First try exact phrase in title or description
    servicesQuery = servicesQuery.or(`title.ilike.${exactPhrase},description.ilike.${exactPhrase}`);

    // If multiple words, also search for each word individually
    if (words.length > 1) {
      // Build conditions for each word in title and description
      const wordConditions = words.map(word => {
        const pattern = `%${word}%`;
        return `or(title.ilike.${pattern},description.ilike.${pattern})`;
      }).join(',');
      
      servicesQuery = servicesQuery.or(wordConditions);
    }

    servicesQuery = servicesQuery.limit(10);
    promises.push(servicesQuery);

    // Similar approach for quick links
    let quickLinksQuery = supabase
      .from('quick_links')
      .select('id, title, description, url, icon, bg_color, text_color')
      .eq('active', true);

    // First try exact phrase in title or description
    quickLinksQuery = quickLinksQuery.or(`title.ilike.${exactPhrase},description.ilike.${exactPhrase}`);

    // If multiple words, also search for each word individually
    if (words.length > 1) {
      // Build conditions for each word in title and description
      const wordConditions = words.map(word => {
        const pattern = `%${word}%`;
        return `or(title.ilike.${pattern},description.ilike.${pattern})`;
      }).join(',');
      
      quickLinksQuery = quickLinksQuery.or(wordConditions);
    }

    quickLinksQuery = quickLinksQuery.limit(10);
    promises.push(quickLinksQuery);

    promises.push(
      supabase
        .from('departments')
        .select('id, name, description')
        .ilike('name', searchTerm)
        .or(`description.ilike.${searchTerm}`)
        .limit(10)
    );

    // Documents search with enhanced number and year handling
    let documentsQuery = supabase
      .from('documents')
      .select(`
        id,
        title,
        description,
        file_url,
        file_type,
        file_size,
        number,
        year,
        published_at,
        subcategory:document_subcategories!inner(
          id,
          name_singular,
          name_plural,
          slug,
          category:document_categories!inner(
            id,
            name,
            slug
          )
        )
      `)
      .eq('active', true)
      .not(biddingDocIds.length > 0 ? 'id' : 'id', biddingDocIds.length > 0 ? `in.(${biddingDocIds.join(',')})` : 'is.null');

    // Handle document type + number search (e.g., "Lei 1443")
    if (isDocTypeNumberSearch && docNumber) {
      documentsQuery = documentsQuery
        .eq('number', docNumber)
        .ilike('subcategory.name_singular', `%${docType}%`);
    }
    // Handle numbers in query - could be document numbers or years
    else if (queryNumbers.length > 0) {
      const conditions = [];
      
      // Check for document numbers
      for (const num of queryNumbers) {
        conditions.push(`number.eq.${num}`);
      }
      
      // Check for years
      for (const num of queryNumbers) {
        if (num >= 1900 && num <= new Date().getFullYear()) {
          conditions.push(`year.eq.${num}`);
        }
      }
      
      if (conditions.length > 0) {
        documentsQuery = documentsQuery.or(conditions.join(','));
      }
    }
    // Use regular text search if no numbers found
    else {
      // Try exact match first
      if (query.trim()) {
        documentsQuery = documentsQuery.or(
          `title.ilike.${searchTerm},description.ilike.${searchTerm}`
        );
      }

      // Then try individual word matches
      const textTerms = words.filter(word => isNaN(Number(word)));
      if (textTerms.length > 0) {
        const wordConditions = textTerms.map(term => {
          const pattern = `%${term}%`;
          return `or(title.ilike.${pattern},description.ilike.${pattern})`;
        }).join(',');
        
        documentsQuery = documentsQuery.or(wordConditions);
      }
    }

    documentsQuery = documentsQuery
      .order('year', { ascending: false })
      .order('number', { ascending: false })
      .limit(30);

    promises.push(documentsQuery);

    const responses = await Promise.all(promises);
    let formattedResults: SearchResult[] = [];

    responses.forEach((response, index) => {
      if (response.error) throw response.error;
      
      response.data.forEach((item: any) => {
        let type: 'news' | 'event' | 'service' | 'department' | 'document' | 'bidding' | 'quicklink';
        let title = '';
        let description = '';
        let date = '';
        let category = '';
        let url = '/';
        let image_url = '';
        let thumbnail_url = '';
        let slug = '';
        let icon = '';
        let bg_color = '';
        let text_color = '';
        let bidding_date = '';
        let location = '';
        let number = null;
        let year = null;
        
        switch (index) {
          case 0: // biddings
            type = 'bidding';
            // Get document number and year from the nested relation
            number = item.document?.number;
            year = item.document?.year;
            
            title = number && year 
              ? `${item.bidding_type || 'Licitação'} ${String(number).padStart(number >= 100 ? 3 : 2, '0')}/${year}`
              : item.title;
            
            description = item.object || item.description;
            bidding_date = item.bidding_date;
            location = item.location;
            url = `/licitacoes?id=${item.id}`;
            break;
          case 1: // news
            type = 'news';
            title = item.title;
            // Safely handle content - check if it exists and is a string
            description = item.content && typeof item.content === 'string' 
              ? sanitizeHtml(item.content.replace(/<[^>]*>/g, ''))
              : '';
            date = item.updated_at || item.created_at;
            category = item.categories?.name;
            url = `/noticias/${item.slug}`;
            image_url = item.image_url;
            thumbnail_url = item.thumbnail_url;
            slug = item.slug;
            break;
          case 2: // events
            type = 'event';
            title = item.title;
            description = item.description;
            date = item.date;
            url = `/eventos/${item.id}`;
            break;
          case 3: // services
            type = 'service';
            title = item.title;
            description = item.description;
            url = item.link;
            icon = item.icon;
            break;
          case 4: // quick_links
            type = 'quicklink';
            title = item.title;
            description = item.description;
            url = item.url;
            icon = item.icon;
            bg_color = item.bg_color;
            text_color = item.text_color;
            break;
          case 5: // departments
            type = 'department';
            title = item.name;
            description = item.description;
            url = `/departamentos/${item.id}`;
            break;
          case 6: // documents
            type = 'document';
            title = item.number && item.year 
              ? `${item.subcategory?.name_singular} n° ${item.number}/${item.year}`
              : item.title;
            description = item.description;
            date = item.published_at;
            category = item.subcategory?.category?.name;
            url = item.file_url;
            number = item.number;
            year = item.year;
            break;
          default:
            type = 'news';
        }

        formattedResults.push({
          id: item.id,
          title,
          description,
          type,
          date,
          url,
          category,
          fileType: item.file_type,
          fileSize: item.file_size,
          number,
          year,
          image_url,
          thumbnail_url,
          slug,
          icon,
          bg_color,
          text_color,
          bidding_date,
          location,
          subcategory: item.subcategory
        });
      });
    });

    // Remove duplicates (sometimes the word-by-word search can return the same item multiple times)
    formattedResults = formattedResults.filter((result, index, self) =>
      index === self.findIndex((r) => r.id === result.id && r.type === result.type)
    );

    // Prioritize documents in results when:
    // 1. Search is for document type + number (e.g., "Lei 1443")
    // 2. Search contains numbers that match document numbers or years
    if (isDocTypeNumberSearch || queryNumbers.length > 0) {
      formattedResults.sort((a, b) => {
        // First sort by type (documents first)
        if (a.type === 'document' && b.type !== 'document') return -1;
        if (a.type !== 'document' && b.type === 'document') return 1;
        
        // For documents, prioritize exact number matches
        if (a.type === 'document' && b.type === 'document') {
          // If searching for a specific document number
          if (queryNumbers.length > 0) {
            const aMatchesNumber = a.number !== null && queryNumbers.includes(a.number);
            const bMatchesNumber = b.number !== null && queryNumbers.includes(b.number);
            
            if (aMatchesNumber && !bMatchesNumber) return -1;
            if (!aMatchesNumber && bMatchesNumber) return 1;
          }
          
          // Sort by year (desc) then by number (desc)
          if (a.year !== b.year) return (b.year || 0) - (a.year || 0);
          return (b.number || 0) - (a.number || 0);
        }
        
        return 0;
      });
    }

    return formattedResults;
  } catch (error) {
    console.error('Error searching content:', error);
    return [];
  }
};

/**
 * Organizes results into filtered categories based on the active tab
 */
export const organizeSearchResults = (
  results: SearchResult[], 
  activeTab: string
): SearchResult[] | { documentsByCategory: Record<string, Record<string, SearchResult[]>> } => {
  const documentsByCategory: Record<string, Record<string, SearchResult[]>> = {};
  const newsResults = results.filter(r => r.type === 'news').slice(0, 5);
  const biddingResults = results.filter(r => r.type === 'bidding').slice(0, 5);
  const serviceResults = results.filter(r => r.type === 'service').slice(0, 5);
  const quickLinkResults = results.filter(r => r.type === 'quicklink').slice(0, 5);

  // Organize documents by category and subcategory
  results.filter(r => r.type === 'document').forEach(doc => {
    if (!doc.subcategory) return;
    
    const catName = doc.subcategory.category.name;
    const subCatName = doc.subcategory.name_plural;
    
    if (!documentsByCategory[catName]) {
      documentsByCategory[catName] = {};
    }
    
    if (!documentsByCategory[catName][subCatName]) {
      documentsByCategory[catName][subCatName] = [];
    }
    
    // Only add if we haven't reached the limit for this subcategory
    if (documentsByCategory[catName][subCatName].length < 5) {
      documentsByCategory[catName][subCatName].push(doc);
    }
  });

  if (activeTab === 'all') {
    const allResults: SearchResult[] = [];
    // Add 3 results of each type (up to a maximum of 12 total)
    allResults.push(...newsResults.slice(0, 3));
    allResults.push(...biddingResults.slice(0, 3));
    allResults.push(...serviceResults.slice(0, 3));
    allResults.push(...quickLinkResults.slice(0, 3));
    
    // Add documents (up to 3 from each subcategory)
    Object.values(documentsByCategory).forEach(subcats => {
      Object.values(subcats).forEach(docs => {
        allResults.push(...docs.slice(0, 3));
      });
    });
    
    return allResults.slice(0, 12);
  } else if (activeTab === 'documents') {
    return { documentsByCategory };
  } else if (activeTab === 'news') {
    return newsResults;
  } else if (activeTab === 'biddings') {
    return biddingResults;
  } else if (activeTab === 'services') {
    return [...serviceResults, ...quickLinkResults].slice(0, 5);
  }

  return [];
};